// Financeiro › Cadastros › Avisos — como cada aviso automático funciona: se está
// ligado, quem recebe, por onde sai (e-mail, sino, Telegram), em que dias e a que
// horas (os agendados) e os parâmetros próprios de cada tipo.
// O catálogo (nome, descrição, canais possíveis, parâmetros, padrão) vem do
// backend (dre-engine/handlers/avisosCatalogo.js) — uma fonte só, então tipo novo
// aparece aqui sem mexer nesta tela. Os avisos em si moram em financeiro.avisos.
// Lista de e-mails vazia = ninguém recebe; a tela pede confirmação antes de salvar assim.
// Salvar atualiza SÓ o cartão salvo: recarregar tudo descartaria o que a pessoa
// digitou nos outros cartões.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Alert, Button, Card, Checkbox, Col, Input, Modal, Row, Select, Space, Spin, Switch,
  Tag, TimePicker, Typography, App as AntApp,
} from 'antd';
import { API_URL } from '../config';
import { usePerms } from '../hooks/usePerms';

const { Text, Title } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

type Valor = string | number;

interface Canal { canal: string; rotulo: string }
interface Dia { dia: number; rotulo: string }
interface Parametro {
  chave: string; rotulo: string; tipo: string; obrigatorio: boolean;
  opcoes: { valor: Valor; rotulo: string }[];
}
interface EntradaCatalogo {
  tipo: string; nome: string; descricao: string;
  natureza: 'agendado' | 'evento';
  canais_possiveis: string[]; canais_fixos?: boolean;
  multiplo: boolean; pode_testar: boolean;
  parametros: Parametro[];
  padrao: { canais: string[]; dias_semana: number[] | null; horario: string | null; params: Record<string, Valor[]> };
}
interface Ultima {
  data_ref: string; status: 'rodando' | 'enviado' | 'nada' | 'erro';
  resumo: string | null; erro: string | null; quando: string; tentativas: number;
}
interface Aviso {
  id: string; tipo: string; nome: string | null; ativo: boolean;
  canais: string[]; dias_semana: number[] | null; horario: string | null;
  params: Record<string, Valor[]>; emails: string[];
  pode_excluir: boolean; ultima: Ultima | null; proxima: string | null;
}
// O que a pessoa está editando num cartão (sempre com os campos preenchidos).
interface Edicao {
  nome: string; ativo: boolean; canais: string[]; dias_semana: number[];
  horario: string; params: Record<string, Valor[]>; emails: string[];
}
interface Rascunho { key: string; tipo: string }

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const TZ = 'America/Sao_Paulo';
const DIAS_UTEIS = [1, 2, 3, 4, 5];
const TODOS_OS_DIAS = [0, 1, 2, 3, 4, 5, 6];

function aEdicao(base: { nome: string | null; ativo: boolean; canais: string[]; dias_semana: number[] | null;
  horario: string | null; params: Record<string, Valor[]>; emails: string[] }, cat: EntradaCatalogo): Edicao {
  const params: Record<string, Valor[]> = {};
  for (const p of cat.parametros) params[p.chave] = [...(base.params?.[p.chave] ?? [])];
  return {
    nome: base.nome ?? '',
    ativo: base.ativo,
    canais: [...base.canais],
    dias_semana: [...(base.dias_semana ?? [])],
    horario: base.horario ?? '',
    params,
    emails: [...base.emails],
  };
}

// Forma comparável: a ordem de canais, dias, opções e e-mails não conta.
function assinatura(e: Edicao): string {
  const num = (a: number[]) => [...a].sort((x, y) => x - y);
  const params = Object.keys(e.params).sort().map(k => [k, [...e.params[k]].map(String).sort()]);
  return JSON.stringify([
    e.nome.trim(), e.ativo, [...e.canais].sort(), num(e.dias_semana), e.horario, params,
    e.emails.map(x => x.trim().toLowerCase()).filter(Boolean).sort(),
  ]);
}

// Datas sempre no fuso de São Paulo (nunca toISOString).
function partes(iso: string): Record<string, string> | null {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const p: Record<string, string> = {};
  for (const x of new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ, weekday: 'long', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d)) p[x.type] = x.value;
  return p;
}
function fmtQuando(iso: string): string {
  const p = partes(iso);
  return p ? `${p.day}/${p.month} às ${p.hour}:${p.minute}` : '—';
}
function fmtProxima(iso: string): string {
  const p = partes(iso);
  return p ? `${p.weekday}, ${p.day}/${p.month} às ${p.hour}:${p.minute}` : '—';
}

// 'HH:MM' → dayjs de hoje naquele horário (sem depender do plugin de formato do dayjs).
function horaParaDayjs(hhmm: string) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  return m ? dayjs().hour(Number(m[1])).minute(Number(m[2])).second(0).millisecond(0) : null;
}

function lerResposta(res: any): any {
  return res.data.body ? JSON.parse(res.data.body) : res.data;
}

interface CartaoProps {
  cat: EntradaCatalogo;
  aviso: Aviso | null; // null = rascunho ainda não salvo
  podeExcluir: boolean;
  edicao: Edicao;
  mudou: boolean;
  travado: boolean;
  salvando: boolean;
  canais: Canal[];
  dias: Dia[];
  sugestoes: { value: string }[];
  onChange: (patch: Partial<Edicao>) => void;
  onSalvar: () => void;
  onTestar: () => void;
  onExcluir: () => void;
  onDescartar: () => void;
}

function CartaoAviso(p: CartaoProps) {
  const { cat, aviso, edicao: e, travado } = p;
  const desab = travado || p.salvando;
  // O que a pessoa digitou em "Quem recebe" e ainda não virou etiqueta. Quem
  // digita o e-mail e clica direto em Salvar não pode perdê-lo: ao sair do
  // campo, o texto que for um e-mail entra na lista.
  const [busca, setBusca] = useState('');
  const promoverBusca = () => {
    const v = busca.trim().toLowerCase();
    setBusca('');
    if (v && EMAIL_RE.test(v) && !e.emails.some(x => x.trim().toLowerCase() === v)) p.onChange({ emails: [...e.emails, v] });
  };
  const rotuloCanal = (c: string) => p.canais.find(x => x.canal === c)?.rotulo || c;
  const nomeExibido = e.nome.trim() || cat.nome;
  const agendado = cat.natureza === 'agendado';
  const canaisPossiveis = p.canais.filter(c => cat.canais_possiveis.includes(c.canal));
  const label = (t: string) => <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{t}</Text>;

  const rodape = () => {
    if (!agendado || !aviso) return null;
    const u = aviso.ultima;
    let ultima: React.ReactNode;
    if (!u) ultima = <Text type="secondary">ainda não saiu</Text>;
    else {
      const quando = fmtQuando(u.quando);
      if (u.status === 'erro') ultima = <Text type="danger">{quando} — {u.erro || 'não saiu'}</Text>;
      else if (u.status === 'rodando') ultima = <Text>{quando} — em andamento</Text>;
      else if (u.status === 'nada') ultima = <Text type="secondary">{quando} — {u.resumo || 'nada para avisar'}</Text>;
      else ultima = <Text>{quando} — {u.resumo || 'enviado'}</Text>;
    }
    const proxima = !aviso.ativo ? 'desligado' : aviso.proxima ? fmtProxima(aviso.proxima) : 'sem próxima marcada';
    return (
      <Col span={24}>
        <Text style={{ fontSize: 12 }}>Última vez: {ultima}</Text>
        <br />
        <Text style={{ fontSize: 12 }} type="secondary">Próxima: {proxima}</Text>
      </Col>
    );
  };

  return (
    <Card size="small"
      title={<Space wrap size={6}>
        <Switch size="small" checked={e.ativo} disabled={desab}
          onChange={(v) => p.onChange({ ativo: v })} />
        <span>{nomeExibido}</span>
        {e.canais.map(c => <Tag key={c} style={{ marginInlineEnd: 0 }}>{rotuloCanal(c)}</Tag>)}
        {!e.ativo && <Tag color="default" style={{ marginInlineEnd: 0 }}>desligado</Tag>}
        {!aviso && <Tag color="gold" style={{ marginInlineEnd: 0 }}>novo — ainda não salvo</Tag>}
      </Space>}
      >
      <Row gutter={[16, 12]}>
        <Col span={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>{cat.descricao}</Text>
        </Col>

        <Col xs={24} lg={12}>
          {label('Nome')}
          <Input value={e.nome} maxLength={80} placeholder={cat.nome} disabled={desab}
            onChange={(ev) => p.onChange({ nome: ev.target.value })} />
        </Col>

        <Col xs={24} lg={12}>
          {label('Quem recebe')}
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Adicionar e-mail…"
            value={e.emails}
            onChange={(v) => { setBusca(''); p.onChange({ emails: v as string[] }); }}
            searchValue={busca}
            onSearch={setBusca}
            onBlur={promoverBusca}
            options={p.sugestoes}
            tokenSeparators={[',', ';', ' ']}
            disabled={desab}
          />
          {!e.emails.length && (
            <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
              Lista vazia — ninguém recebe este aviso.
            </Text>
          )}
        </Col>

        <Col xs={24} lg={12}>
          {label('Por onde')}
          {cat.canais_fixos ? (
            <>
              <Checkbox.Group value={e.canais} disabled
                options={canaisPossiveis.map(c => ({ value: c.canal, label: c.rotulo }))} />
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                Neste aviso os canais são fixos.
              </Text>
            </>
          ) : (
            <Checkbox.Group value={e.canais} disabled={desab}
              onChange={(v) => p.onChange({ canais: v as string[] })}
              options={canaisPossiveis.map(c => ({ value: c.canal, label: c.rotulo }))} />
          )}
        </Col>

        <Col xs={24} lg={12}>
          {label('Quando')}
          {!agendado ? (
            <Text style={{ fontSize: 13 }}>Na hora em que acontece.</Text>
          ) : (
            <Space orientation="vertical" size={6} style={{ width: '100%' }}>
              <Checkbox.Group value={e.dias_semana} disabled={desab}
                onChange={(v) => p.onChange({ dias_semana: v as number[] })}
                options={p.dias.map(d => ({ value: d.dia, label: d.rotulo }))} />
              <Space size={12} wrap>
                <Button type="link" size="small" style={{ padding: 0 }} disabled={desab}
                  onClick={() => p.onChange({ dias_semana: DIAS_UTEIS })}>Dias úteis</Button>
                <Button type="link" size="small" style={{ padding: 0 }} disabled={desab}
                  onClick={() => p.onChange({ dias_semana: TODOS_OS_DIAS })}>Todos os dias</Button>
                <TimePicker
                  format="HH:mm" minuteStep={15} allowClear={false} needConfirm={false}
                  placeholder="Horário" disabled={desab}
                  value={horaParaDayjs(e.horario)}
                  onChange={(v) => p.onChange({ horario: v ? v.format('HH:mm') : '' })} />
              </Space>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Sai na primeira verificação depois do horário (o sistema confere de 15 em 15 minutos).
              </Text>
            </Space>
          )}
        </Col>

        {cat.parametros.filter(pr => pr.tipo === 'multi').map(pr => (
          <Col key={pr.chave} xs={24} lg={12}>
            {label(pr.rotulo)}
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              placeholder="Escolher…"
              value={e.params[pr.chave] || []}
              onChange={(v) => p.onChange({ params: { ...e.params, [pr.chave]: v as Valor[] } })}
              options={pr.opcoes.map(o => ({ value: o.valor, label: o.rotulo }))}
              disabled={desab}
            />
          </Col>
        ))}

        {rodape()}

        {/* Ações no rodapé, e não no cabeçalho: no celular três botões ali espremiam o nome do aviso. */}
        {!travado && (
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              {cat.pode_testar && aviso && !p.mudou && (
                <Button size="small" disabled={p.salvando} onClick={p.onTestar}>Enviar teste para mim</Button>
              )}
              {aviso && p.podeExcluir && (
                <Button size="small" danger disabled={p.salvando} onClick={p.onExcluir}>Excluir</Button>
              )}
              {!aviso && <Button size="small" disabled={p.salvando} onClick={p.onDescartar}>Descartar</Button>}
              <Button type="primary" size="small" loading={p.salvando}
                disabled={!p.mudou} onClick={p.onSalvar}>
                Salvar
              </Button>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  );
}

function FinanceiroAvisosInner() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podePerfil = pode('financeiro.avisos.editar') || pode('admin.permissoes.editar');

  const [catalogo, setCatalogo] = useState<EntradaCatalogo[]>([]);
  const [canais, setCanais] = useState<Canal[]>([]);
  const [dias, setDias] = useState<Dia[]>([]);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [rascunhos, setRascunhos] = useState<Rascunho[]>([]);
  const [podeEditarApi, setPodeEditarApi] = useState(false);
  const [cadastroAusente, setCadastroAusente] = useState(false);
  const [loading, setLoading] = useState(true);
  // Falha ao carregar: a tela DIZ que falhou (com "Tentar de novo") — nunca mostra
  // lista vazia nem "você não pode alterar", que é outra resposta.
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  // O verificador (que roda os avisos com hora marcada, de 15 em 15 min) deixa um
  // sinal de vida; se ele parou, a tela diz — senão o silêncio pareceria normal.
  const [verificador, setVerificador] = useState<{ ultima: string | null; parado: boolean } | null>(null);
  // O que está em edição por cartão (chave = id do aviso ou chave do rascunho).
  const [edicoes, setEdicoes] = useState<Record<string, Edicao>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [novoTipo, setNovoTipo] = useState<string | undefined>(undefined);
  const [contador, setContador] = useState(0);

  const podeEditar = podePerfil && podeEditarApi && !cadastroAusente;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    setErroCarga(null);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/financeiro/avisos`, { headers: { Authorization: token } });
      const data = lerResposta(res);
      // 200 com corpo de erro (ou sem a lista) conta como falha, não como "nenhum aviso".
      if (!data || !Array.isArray(data.catalogo) || !Array.isArray(data.avisos)) {
        throw new Error(data?.error || 'resposta sem a lista de avisos');
      }
      const cat: EntradaCatalogo[] = data.catalogo;
      const lista: Aviso[] = data.avisos;
      setCatalogo(cat);
      setCanais(data.canais || []);
      setDias(data.dias || []);
      setAvisos(lista);
      setPodeEditarApi(!!data.pode_editar);
      setCadastroAusente(!!data.cadastro_ausente);
      setVerificador(data.verificador || null);
      const ed: Record<string, Edicao> = {};
      for (const a of lista) {
        const c = cat.find(x => x.tipo === a.tipo);
        if (c) ed[a.id] = aEdicao(a, c);
      }
      setEdicoes(ed);
      setRascunhos([]);
    } catch (e: any) {
      setErroCarga(e?.response?.data?.error || e.message || 'falha de conexão');
    } finally { setLoading(false); }
  }

  const catPorTipo = useMemo(() => {
    const m: Record<string, EntradaCatalogo> = {};
    for (const c of catalogo) m[c.tipo] = c;
    return m;
  }, [catalogo]);

  // Sugestões do dropdown: todo e-mail que já aparece em algum aviso.
  const sugestoes = useMemo(() => {
    const s = new Set<string>();
    for (const a of avisos) for (const e of a.emails) s.add(e);
    for (const ed of Object.values(edicoes)) for (const e of ed.emails) s.add(e);
    return Array.from(s).sort().map(v => ({ value: v }));
  }, [avisos, edicoes]);

  // Rascunhos no topo; depois os salvos na ordem do catálogo.
  const cartoes = useMemo(() => {
    const ordem = (tipo: string) => catalogo.findIndex(c => c.tipo === tipo);
    const salvos = avisos
      .filter(a => catPorTipo[a.tipo])
      .slice()
      .sort((a, b) => ordem(a.tipo) - ordem(b.tipo));
    return [
      ...rascunhos.filter(r => catPorTipo[r.tipo]).map(r => ({ key: r.key, aviso: null as Aviso | null, tipo: r.tipo })),
      ...salvos.map(a => ({ key: a.id, aviso: a as Aviso | null, tipo: a.tipo })),
    ];
  }, [avisos, rascunhos, catalogo, catPorTipo]);

  function salvoDe(aviso: Aviso | null, cat: EntradaCatalogo): Edicao {
    return aviso
      ? aEdicao(aviso, cat)
      : aEdicao({ nome: null, ativo: true, canais: cat.padrao.canais, dias_semana: cat.padrao.dias_semana,
        horario: cat.padrao.horario, params: cat.padrao.params || {}, emails: [] }, cat);
  }

  function mudou(key: string, aviso: Aviso | null, cat: EntradaCatalogo): boolean {
    if (!aviso) return true; // rascunho: só vai ao servidor ao salvar
    const atual = edicoes[key];
    return !!atual && assinatura(atual) !== assinatura(salvoDe(aviso, cat));
  }

  function alterar(key: string, patch: Partial<Edicao>) {
    setEdicoes(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function criarRascunho() {
    const cat = novoTipo ? catPorTipo[novoTipo] : null;
    if (!cat) return;
    const key = `novo:${cat.tipo}:${contador}`;
    setContador(contador + 1);
    setRascunhos(prev => [{ key, tipo: cat.tipo }, ...prev]);
    setEdicoes(prev => ({ ...prev, [key]: salvoDe(null, cat) }));
    setNovoAberto(false);
    setNovoTipo(undefined);
  }

  function descartar(key: string) {
    setRascunhos(prev => prev.filter(r => r.key !== key));
    setEdicoes(prev => { const n = { ...prev }; delete n[key]; return n; });
  }

  async function salvar(key: string, aviso: Aviso | null, cat: EntradaCatalogo) {
    const ed = edicoes[key];
    if (!ed) return;
    const emails = ed.emails.map(x => x.trim().toLowerCase()).filter(Boolean);
    const invalido = emails.find(x => !EMAIL_RE.test(x));
    if (invalido) {
      message.error(`"${invalido}" não parece um e-mail. Corrija antes de salvar.`);
      return;
    }
    const agendado = cat.natureza === 'agendado';
    if (ed.ativo) {
      if (agendado && !ed.dias_semana.length) {
        message.error('Marque pelo menos um dia da semana — ou desligue o aviso.');
        return;
      }
      if (agendado && !ed.horario) {
        message.error('Escolha o horário em que o aviso sai.');
        return;
      }
      if (!cat.canais_fixos && !ed.canais.length) {
        message.error('Escolha pelo menos um canal — ou desligue o aviso.');
        return;
      }
    }
    if (ed.ativo && !emails.length) {
      const okVazio = await new Promise<boolean>(resolve => {
        modal.confirm({
          title: 'Deixar a lista vazia?',
          content: `Ninguém vai receber o aviso "${ed.nome.trim() || cat.nome}" até alguém ser cadastrado de novo.`,
          okText: 'Salvar vazia',
          okButtonProps: { danger: true },
          cancelText: 'Voltar',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!okVazio) return;
    }
    const corpo = {
      nome: ed.nome.trim(),
      ativo: ed.ativo,
      canais: cat.canais_fixos ? cat.padrao.canais : ed.canais,
      dias_semana: agendado ? ed.dias_semana : null,
      horario: agendado ? ed.horario : null,
      params: ed.params,
      emails,
    };
    setSalvando(key);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const res = aviso
        ? await axios.put(`${API_URL}/financeiro/avisos/${aviso.id}`, corpo, { headers })
        : await axios.post(`${API_URL}/financeiro/avisos`, { tipo: cat.tipo, ...corpo }, { headers });
      const data = lerResposta(res);
      const salvo: Aviso = data.aviso;
      if (aviso) {
        setAvisos(prev => prev.map(x => (x.id === aviso.id ? salvo : x)));
        setEdicoes(prev => ({ ...prev, [aviso.id]: aEdicao(salvo, cat) }));
      } else {
        setAvisos(prev => [...prev, salvo]);
        setRascunhos(prev => prev.filter(r => r.key !== key));
        setEdicoes(prev => {
          const n = { ...prev };
          delete n[key];
          n[salvo.id] = aEdicao(salvo, cat);
          return n;
        });
      }
      const quando = cat.natureza === 'agendado' && salvo.ativo && salvo.proxima ? ` Próxima vez: ${fmtProxima(salvo.proxima)}.` : '';
      message.success(`Aviso "${salvo.nome || cat.nome}" salvo.${quando}`, quando ? 6 : 3);
    } catch (e: any) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(null); }
  }

  async function testar(aviso: Aviso, cat: EntradaCatalogo) {
    setSalvando(aviso.id);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/financeiro/avisos/${aviso.id}/testar`, {},
        { headers: { Authorization: token } });
      const data = lerResposta(res);
      if (data.ok) message.success(data.mensagem || 'Teste enviado para você.');
      else message.warning(data.mensagem || `Não foi possível enviar o teste de "${cat.nome}".`);
    } catch (e: any) {
      message.error('Erro ao enviar o teste: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(null); }
  }

  function excluir(aviso: Aviso, cat: EntradaCatalogo) {
    const nome = aviso.nome || cat.nome;
    modal.confirm({
      title: 'Excluir este aviso?',
      content: `O aviso "${nome}" deixa de existir e ninguém mais o recebe.`,
      okText: 'Excluir',
      okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: async () => {
        setSalvando(aviso.id);
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/financeiro/avisos/${aviso.id}`, { headers: { Authorization: token } });
          setAvisos(prev => prev.filter(x => x.id !== aviso.id));
          setEdicoes(prev => { const n = { ...prev }; delete n[aviso.id]; return n; });
          message.success(`Aviso "${nome}" excluído.`);
        } catch (e: any) {
          message.error('Erro ao excluir: ' + (e?.response?.data?.error || e.message));
        } finally { setSalvando(null); }
      },
    });
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (erroCarga) {
    return (
      <Alert type="error" showIcon
        title="Não consegui carregar os avisos."
        description={`Nada foi alterado. Motivo: ${erroCarga}`}
        action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
    );
  }

  const catNovo = novoTipo ? catPorTipo[novoTipo] : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <Title level={5} style={{ marginTop: 0 }}>Avisos automáticos</Title>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Cada aviso: o que avisa, quem recebe, por onde e quando. O que você salvar vale a
            partir da próxima vez que o aviso sair.
          </Text>
        </div>
        {podeEditar && (
          <Button type="primary" onClick={() => setNovoAberto(true)}>Novo aviso</Button>
        )}
      </div>
      {verificador?.parado && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          title="Os avisos com hora marcada não estão saindo."
          description={`O verificador que os dispara ${verificador.ultima ? `não roda desde ${fmtQuando(verificador.ultima)}` : 'ainda não rodou nenhuma vez'}. `
            + 'Enquanto isso, contas a pagar do dia e cobrança de horas não saem. Avise quem cuida do sistema.'} />
      )}
      {cadastroAusente && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          title="O cadastro ainda não foi criado no banco (migration pendente). Os avisos seguem como estavam até isso rodar — a edição fica travada até lá." />
      )}
      {!podeEditar && !cadastroAusente && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          title="Você pode ver os avisos, mas não alterá-los (permissão de edição de Avisos)." />
      )}
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        {cartoes.map(c => {
          const cat = catPorTipo[c.tipo];
          const ed = edicoes[c.key];
          if (!cat || !ed) return null;
          return (
            <CartaoAviso key={c.key}
              cat={cat} aviso={c.aviso} edicao={ed}
              // Decidido aqui, não pelo pode_excluir que veio na carga: ao criar o
              // segundo aviso de um tipo, o primeiro passa a poder sair na hora.
              podeExcluir={!!c.aviso && cat.multiplo && avisos.filter(a => a.tipo === c.tipo).length > 1}
              mudou={mudou(c.key, c.aviso, cat)}
              travado={!podeEditar}
              salvando={salvando === c.key}
              canais={canais} dias={dias} sugestoes={sugestoes}
              onChange={(patch) => alterar(c.key, patch)}
              onSalvar={() => salvar(c.key, c.aviso, cat)}
              onTestar={() => c.aviso && testar(c.aviso, cat)}
              onExcluir={() => c.aviso && excluir(c.aviso, cat)}
              onDescartar={() => descartar(c.key)} />
          );
        })}
      </Space>

      <Modal
        title="Novo aviso"
        open={novoAberto}
        width="94vw"
        style={{ maxWidth: 640, top: 16 }}
        okText="Criar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !catNovo }}
        onOk={criarRascunho}
        onCancel={() => { setNovoAberto(false); setNovoTipo(undefined); }}
      >
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="Escolha o que o aviso vai avisar…"
          value={novoTipo}
          onChange={(v) => setNovoTipo(v)}
          options={catalogo.filter(c => c.multiplo).map(c => ({ value: c.tipo, label: c.nome }))}
        />
        {catNovo && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            {catNovo.descricao}
          </Text>
        )}
      </Modal>
    </div>
  );
}

export default function FinanceiroAvisos() {
  return <AntApp><FinanceiroAvisosInner /></AntApp>;
}
